import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Collapsible from '../../components/Collapsible';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { formatCurrency, formatDateForTooltip } from '@/utils/formatUtils';
import { getPriceChartData } from '@/api/card';
import dondonGrey from '@/assets/dondon_grey.svg';

const PSA_GRADES = ['PSA_10', 'PSA_9', 'PSA_8', 'PSA_7', 'PSA_6'];
const NON_PSA_CONDITIONS = ['MINT', 'NEAR_MINT', 'FAIR', 'POOR'];
const TIME_WINDOWS = ['ALL', 'LAST_1_WEEK', 'LAST_1_MONTH', 'LAST_3_MONTHS'];

const PriceChartSection = ({ cardId, cardType = 'SINGLE_CARD', listingType = 'PSA_SINGLE_CARD' }) => {
  const { t } = useTranslation();

  // Generate PSA grades display using translations
  const PSA_GRADES_DISPLAY = [
    t('psa.grades.all'),
    t('psa.grades.psa_10'),
    t('psa.grades.psa_9'),
    t('psa.grades.psa_8'),
    t('psa.grades.psa_7'),
    t('psa.grades.psa_6')
  ];

  // Generate time windows display using translations
  const TIME_WINDOWS_DISPLAY = [
    t('card.time_windows.all'),
    t('card.time_windows.week'),
    t('card.time_windows.month'),
    t('card.time_windows.three_months')
  ];

  const NON_PSA_CONDITIONS_DISPLAY = [
    t('psa.grades.all'),
    t('sell.product_info.conditions_short.mint'),
    t('sell.product_info.conditions_short.near_mint'),
    t('sell.product_info.conditions_short.fair'),
    t('sell.product_info.conditions_short.poor')
  ];
  const [selectedGrade, setSelectedGrade] = useState(t('psa.grades.all'));
  const [selectedTime, setSelectedTime] = useState(t('card.time_windows.all'));

  // Determine if we show PSA grades or non-PSA conditions
  const isPsaListing = listingType === 'PSA_SINGLE_CARD';
  
  // Map display values to API values
  const getTimeWindowParam = () => {
    const index = TIME_WINDOWS_DISPLAY.indexOf(selectedTime);
    return TIME_WINDOWS[index];
  };
  
  const getGradeOrConditionParam = () => {
    if (selectedGrade === t('psa.grades.all')) return null;
    
    if (isPsaListing) {
      const index = PSA_GRADES_DISPLAY.indexOf(selectedGrade);
      return index > 0 ? PSA_GRADES[index - 1] : null;
    } else {
      const index = NON_PSA_CONDITIONS_DISPLAY.indexOf(selectedGrade);
      return index > 0 ? NON_PSA_CONDITIONS[index - 1] : null;
    }
  };
  
  // Fetch chart data
  const { data: chartData, isLoading } = useQuery({
    queryKey: ['priceChart', cardId, listingType, getTimeWindowParam(), getGradeOrConditionParam()],
    queryFn: () => {
      const params = {
        listingType,
        window: getTimeWindowParam()
      };
      
      if (isPsaListing && getGradeOrConditionParam()) {
        params.psaGrade = getGradeOrConditionParam();
      } else if (!isPsaListing && getGradeOrConditionParam()) {
        params.condition = getGradeOrConditionParam();
      }
      
      return getPriceChartData(cardId, params);
    },
    enabled: !!cardId,
  });
  
  // Parse chart data - ensure we handle the correct format
  const formattedChartData = chartData?.data?.points || [];

  // Determine which filter options to show based on listing type
  const filterOptions = isPsaListing ? PSA_GRADES_DISPLAY : NON_PSA_CONDITIONS_DISPLAY;
  const showFilterOptions = listingType !== 'CARD_BOX';

  return (
    <Collapsible title={t('card.price_history')} defaultOpen className="border-0 rounded-none border-t">
      <div className='rounded-md border border-[#eeeeee]'>
        {/* Grade/Condition Tabs */}
        {showFilterOptions && (
          <div className="h-12 px-6 flex items-center justify-between border-b border-[#eeeeee]">
            {filterOptions.map((option) => (
              <button
                key={option}
                onClick={() => setSelectedGrade(option)}
                className={`h-full py-2.5 ${
                  selectedGrade === option 
                    ? 'border-b-2 border-[#111111] text-[#111111] font-bold'
                    : 'text-[#aaaaaa] font-bold'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        )}

        {/* Time Period Selector */}
        <div className="h-10 px-px bg-[#f4f4f4] flex">
          {TIME_WINDOWS_DISPLAY.map((time) => (
            <button
              key={time}
              onClick={() => setSelectedTime(time)}
              className={`flex-1 flex items-center justify-center ${
                selectedTime === time
                  ? 'bg-white rounded m-[3px] text-[#111111]'
                  : 'text-[#111111] m-[3px]'
              }`}
            >
              {time}
            </button>
          ))}
        </div>

        {/* Chart */}
        <div className="p-4 h-[280px]">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <span>Loading...</span>
            </div>
          ) : formattedChartData.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full">
              <img src={dondonGrey} alt="DonDon" className="w-[120px] h-[80px] mb-4" />
              <p className="text-sm text-[#aaaaaa]">{t('common.no_price_trend')}</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={formattedChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eeeeee" />
                <XAxis 
                  dataKey="dt" 
                  stroke="#666666"
                  tick={{ fontSize: 10 }}
                  tickFormatter={(value) => new Date(value).toLocaleDateString(undefined, { month: '2-digit', day: '2-digit' })}
                />
                <YAxis 
                  stroke="#666666"
                  tick={{ fontSize: 10 }}
                  tickFormatter={(value) => formatCurrency(value)}
                />
                <Tooltip 
                  formatter={(value) => [formatCurrency(value), 'Price']}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #dddddd',
                    borderRadius: '4px',
                    padding: '8px'
                  }}
                  labelFormatter={formatDateForTooltip}
                />
                <Line 
                  type="monotone" 
                  dataKey="price" 
                  stroke="#3b5493" 
                  strokeWidth={2}
                  dot={{ fill: '#3b5493', strokeWidth: 2 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </Collapsible>
  );
};

export default PriceChartSection; 